import React from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { countryCodes } from "settings/constants/constants";
import type { UseChangeRequestFormReturn } from "../../hooks/useChangeRequestForm";

interface AttachmentsTabProps {
  isKontrolEdge: boolean;
  form: UseChangeRequestFormReturn;
  isSaving: boolean;
  isAttachmentsValid: () => boolean;
  onBack: () => void;
  onSave: () => void;
}

export const AttachmentsTab: React.FC<AttachmentsTabProps> = ({
  isKontrolEdge,
  form,
  isSaving,
  isAttachmentsValid,
  onBack,
  onSave,
}) => {
  return (
    <div className="space-y-4 text-sm text-gray-700 flex flex-col h-full">
      <div>
        <label className="block mb-1 font-medium text-semantic-text-primary">
          Attach (screenshots, logs, etc.)
        </label>
        <div className="flex flex-col items-start gap-2">
          <label
            htmlFor="file-upload"
            className="
              inline-flex items-center justify-center
              cursor-pointer
              px-4 py-2
              min-w-[140px]
              border border-gray-300
              rounded-sm
              bg-gray-50
              text-sm whitespace-nowrap
              hover:bg-gray-100
            "
          >
            {form.formState.attachmentFiles ? "Change Files" : "Choose Files"}
          </label>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            multiple
            onChange={form.handleFileChange}
          />
          <span className="text-sm text-gray-500 break-all">
            {form.formState.attachmentFiles
              ? Array.from(form.formState.attachmentFiles)
                  .map((file: File) => file.name)
                  .join(", ")
              : "No file chosen"}
          </span>
        </div>
      </div>
      {!isKontrolEdge && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mt-3">
            <span className="font-medium text-sm text-gray-800">
              I want to send a response of the case to another person
            </span>
            <GenericSwitch
              checked={form.formState.sendResponse}
              onChange={form.setSendResponse}
            />
          </div>

          {form.formState.sendResponse && (
            <div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
              <div className="flex gap-4">
                <div className="w-1/2">
                  <label className="block mb-1 font-medium flex items-center gap-1">
                    Contact Name <span className="text-red-500">*</span>
                    <GenericTooltip
                      text="At least 3 characters."
                      position="top"
                    />
                  </label>
                  <GenericInput
                    placeholder="Name"
                    value={form.formState.contactName}
                    onChange={form.setContactName}
                  />
                </div>
                <div className="w-1/2">
                  <label className="block mb-1 font-medium flex items-center gap-1">
                    Contact Email <span className="text-red-500">*</span>
                    <GenericTooltip
                      text="Must be a valid email format."
                      position="top"
                    />
                  </label>
                  <GenericInput
                    placeholder="Email"
                    value={form.formState.contactEmail}
                    onChange={form.setContactEmail}
                    type="email"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-1/2">
                  <label className="block mb-1 font-medium">
                    Country Code
                  </label>
                  <GenericDropdown
                    items={countryCodes.map((c) => c.label)}
                    value={
                      countryCodes.find(
                        (c) => c.value === form.formState.selectedCountryCode
                      )?.label || "United States (+1)"
                    }
                    onSelect={(label: string) => {
                      const found = countryCodes.find(
                        (c) => c.label === label
                      );
                      if (found) {
                        form.setSelectedCountryCode(found.value);
                      }
                    }}
                    defaultLabel="Select country code"
                    minWidthClass=""
                    maxWidthClass=""
                    widthClass="w-full"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block mb-1 font-medium">
                    Phone Number
                  </label>
                  <GenericInput
                    placeholder="Phone number"
                    value={form.formState.contactPhoneNumber}
                    onChange={form.setContactPhoneNumber}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Buttons fixed at bottom */}
      <div className="sticky bottom-0 bg-[var(--color-semantic-bg-primary)] pt-4 pb-2 border-t border-gray-200 -mx-4 px-4 mt-auto">
        <div className="flex justify-end gap-2">
          <GenericButton
            label="Back"
            variant="secondary"
            onClick={onBack}
          />
          <GenericButton
            label="Save"
            variant="brand"
            onClick={onSave}
            disabled={!isAttachmentsValid() || isSaving}
          />
        </div>
      </div>
    </div>
  );
};

